/**
 * Application Constants
 * Centralized configuration for the Notematic landing page
 */

export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'Notematic',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  environment: import.meta.env.VITE_APP_ENV || 'development',
} as const;

export const FEATURES = {
  enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  enableDarkMode: import.meta.env.VITE_ENABLE_DARK_MODE !== 'false', // Default true
} as const;

export const CONTACT = {
  email: 'contact@notematic.com.au',
  phone: '+61 410 346 102',
  address: '231 La Perouse street Red Hill ACT 2603',
} as const;

export const SOCIAL_LINKS = {
  linkedin: 'https://linkedin.com/company/notematic',
  twitter: 'https://twitter.com/notematic',
} as const;

export type NavChild = {
  name: string;
  path: string;
  description?: string;
};

export type NavItem = {
  name: string;
  path?: string;
  children?: NavChild[];
};

export const NAVIGATION: NavItem[] = [
  { name: 'Home', path: '/' },
  {
    name: 'Solutions',
    children: [
      { name: 'For MMHCs', path: '/solutions/mmhc', description: 'Medicare Mental Health Centres' },
      { name: 'For PHNs', path: '/solutions/phn', description: 'Primary Health Networks' },
      { name: 'For Clinicians', path: '/solutions/clinicians', description: 'Individual practitioners' },
    ],
  },
  {
    name: 'Workflows',
    children: [
      { name: 'IAR Documentation', path: '/workflows/iar', description: 'Initial Assessment & Referral' },
      { name: 'Clinical Notes', path: '/workflows/clinical-notes', description: 'SOAP, DAP, BIRP, GIRP, PIRP & PIE' },
    ],
  },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Features', path: '/features' },
];

export const FOOTER_LINKS = {
  legal: [
    { name: 'Privacy Notice', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ],
  company: [
    { name: 'Features', path: '/features' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
  ],
  solutions: [
    { name: 'For MMHCs', path: '/solutions/mmhc' },
    { name: 'For PHNs', path: '/solutions/phn' },
    { name: 'For Clinicians', path: '/solutions/clinicians' },
  ],
};

export const COMPLIANCE_BADGES = [
  {
    name: 'Australian Privacy Principles',
    description: 'Compliant with APP guidelines',
    icon: 'shield',
  },
  {
    name: 'Healthcare Standards',
    description: 'Meets Australian healthcare regulations',
    icon: 'health',
  },
  {
    name: 'Data Encryption',
    description: 'End-to-end encryption',
    icon: 'lock',
  },
  {
    name: 'Secure Infrastructure',
    description: 'Azure-hosted with enterprise security',
    icon: 'server',
  },
] as const;

export const FEATURES_LIST = [
  {
    title: 'Multi-Party Consent',
    description: 'Secure consent capture and management for all session participants',
    icon: 'users', // SVG icon name
  },
  {
    title: 'Secure Recording',
    description: 'Audio processed securely in-memory only — never stored persistently. Encrypted in transit via TLS 1.2+.',
    icon: 'microphone', // SVG icon name
  },
  {
    title: 'AI-Assisted Notes',
    description: 'Generate draft clinical notes in 7 formats — IAR, SOAP, DAP, BIRP, GIRP, PIRP & PIE',
    icon: 'document', // SVG icon name
  },
  {
    title: 'Clinician Review',
    description: 'Mandatory review and approval before export',
    icon: 'check', // SVG icon name
  },
  {
    title: 'Audit Trail',
    description: 'Immutable logging of all actions and access',
    icon: 'history', // SVG icon name
  },
  {
    title: 'Privacy First',
    description: 'Built for Australian privacy and healthcare compliance',
    icon: 'shield', // SVG icon name
  },
] as const;

